import _ from 'lodash';
import { initialize, change } from 'redux-form';

import { loading, success, handleErrorException } from 'State/actions';
import { DailyDepositFieldEnum, MealSummaryFieldEnum } from 'Common/FieldEnum';
import { DAILY_DEPOSIT_FORM_NAME } from 'Common/FormEnum';
import { TitleEnum } from 'Common/Constants/Enumeration/CommonEnum';
import { addNewChecks } from 'Pages/mealsummary/helpers/helper';
import { getPreferenceEnabled } from 'State/selectors';
import { PreferencesEnum } from 'Common/Security/PreferencesEnum';

import * as DailyDepositSelectors from './selectors';
import * as types from './types';

export const resetDailyDeposit = () => ({
  type: types.DAILY_DEPOSIT_CLEAR_STATE,
});

export const setSaveSuccessful = payload => ({
  type: types.DAILY_DEPOSIT_SET_SAVE_SUCCESSFUL,
  payload,
});

export const setCloseSuccessful = payload => ({
  type: types.DAILY_DEPOSIT_SET_CLOSE_SUCCESSFUL,
  payload,
});

export const getPrimarySites = () => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(resetDailyDeposit());
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, true));

    try {
      const response = await MPWWebApi.getGeneric(
        `DailyDeposit/GetPrimarySites`
      );
      dispatch({
        type: types.DAILY_DEPOSIT_GET_SITES,
        payload: response.data.Data,
      });
    } catch (ex) {
      const defaultError = {
        Title: TitleEnum.Error,
        Message: 'Unable to retrieve sites.',
      };

      dispatch(
        handleErrorException(ex, types.DAILY_DEPOSIT_FETCH_ERROR, defaultError)
      );
    } finally {
      dispatch(loading(types.DAILY_DEPOSIT_FETCHING, false));
    }
  };
};

export const getDailyDepositData = (siteId, date, isSupper) => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    const state = getState();
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, true));

    if (siteId && date && !_.isNil(isSupper)) {
      const params = {
        PrimarySiteId: siteId,
        ServingDT: date,
        IsSupper: isSupper,
      };

      const [
        dailyDepositResponse,
        mealSummariesResponse,
        getChecksResponse,
      ] = await Promise.all([
        MPWWebApi.postGenericInsertUpdate(
          `DailyDeposit/GetDailyDeposit`,
          params
        ),
        MPWWebApi.postGenericInsertUpdate(
          `DailyDeposit/GetMealSummaries`,
          params
        ),
        MPWWebApi.postGenericInsertUpdate(`DailyDeposit/GetChecks`, params),
      ]);

      const mealSummaries = mealSummariesResponse.data.Data || [];

      dispatch(success(types.DAILY_DEPOSIT_GET_SUMMARIES, mealSummaries));

      const checks = getChecksResponse.data.Data;
      const dailyDeposit = dailyDepositResponse.data.Data;
      dailyDeposit[DailyDepositFieldEnum.Checks] = checks;

      const hasSavedDailyDeposit = Boolean(
        dailyDeposit[DailyDepositFieldEnum.Deposit]
      );

      /** user has not saved their daily deposit */
      if (!hasSavedDailyDeposit) {
        // Check if the user has the preference to autopopulate deposit from meal summaries
        const autoPopulate = getPreferenceEnabled(
          state,
          PreferencesEnum.AutopopulateDeposit
        );
        if (autoPopulate) {
          /** Calculate deposit amount by summing the differce of all the meal summary Close and Open amounts */
          const depositFromSummaries = _.sumBy(mealSummaries, x => {
            const closeAmount =
              Number(x[MealSummaryFieldEnum.CloseAmount]) || 0;
            const openAmount = Number(x[MealSummaryFieldEnum.OpenAmount]) || 0;

            return closeAmount - openAmount;
          });
          dailyDeposit[DailyDepositFieldEnum.Deposit] = depositFromSummaries;
        }
      }

      if (!dailyDeposit[DailyDepositFieldEnum.Deposit]) {
        dailyDeposit[DailyDepositFieldEnum.Deposit] = 0;
      }

      dispatch(success(types.DAILY_DEPOSIT_GET_DATA, dailyDeposit));
    }
  } catch (ex) {
    const defaultError = {
      Title: TitleEnum.Error,
      Message: 'Unable to retrieve daily deposit.',
    };

    dispatch(
      handleErrorException(ex, types.DAILY_DEPOSIT_FETCH_ERROR, defaultError)
    );
  } finally {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, false));
  }
};

export const getCheckData = (siteId, date, isSupper) => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    const state = getState();
    const currentChecks = DailyDepositSelectors.getChecks(state);
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, true));

    if (siteId && date && !_.isNil(isSupper)) {
      const params = {
        PrimarySiteId: siteId,
        ServingDT: date,
        IsSupper: isSupper,
      };

      const getChecksResponse = await MPWWebApi.postGenericInsertUpdate(
        `DailyDeposit/GetChecks`,
        params
      );

      const checks = getChecksResponse.data.Data;

      dispatch(
        change(
          DAILY_DEPOSIT_FORM_NAME,
          DailyDepositFieldEnum.Checks,
          addNewChecks(currentChecks, checks)
        )
      );
    }
  } catch (ex) {
    const defaultError = {
      Title: TitleEnum.Error,
      Message: 'Unable to retrieve checks.',
    };

    dispatch(
      handleErrorException(ex, types.DAILY_DEPOSIT_FETCH_ERROR, defaultError)
    );
  } finally {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, false));
  }
};

export const getWarnings = (siteId, date, isSupper) => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, true));

    if (siteId && date && !_.isNil(isSupper)) {
      const params = {
        PrimarySiteId: siteId,
        ServingDT: date,
        IsSupper: isSupper,
      };

      const depositWarningsResponse = await MPWWebApi.postGenericInsertUpdate(
        `DailyDeposit/GetDailyDepositWarnings`,
        params
      );

      const CEPValidationResponse = await MPWWebApi.postGenericInsertUpdate(
        `DailyDeposit/GetCEPValidation`,
        params
      );

      const warnings = depositWarningsResponse.data.Data;
      const CEPErrors = CEPValidationResponse.data.Data;

      dispatch(
        success(types.DAILY_DEPOSIT_GET_WARNINGS, { warnings, CEPErrors })
      );
    }
  } catch (ex) {
    const defaultError = {
      Title: TitleEnum.Error,
      Message: 'Error validating deposit.',
    };

    dispatch(
      handleErrorException(ex, types.DAILY_DEPOSIT_FETCH_ERROR, defaultError)
    );
  } finally {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, false));
  }
};

export const getDailyDepositReport = values => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, true));
    const reportResponse = await MPWWebApi.postGenericInsertUpdate(
      `DailyDeposit/GetDailyDepositReport`,
      values
    );

    const report = reportResponse.data.Data;
    dispatch(
      success(types.DAILY_DEPOSIT_GET_REPORT, report ? report : 'No Data')
    );
  } catch (ex) {
    const defaultError = {
      Title: TitleEnum.Error,
      Message: 'Error retrieving report.',
    };
    dispatch(
      handleErrorException(ex, types.DAILY_DEPOSIT_FETCH_ERROR, defaultError)
    );
  } finally {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, false));
  }
};

export const getBankDepositReport = (siteId, date, isSupper) => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, true));

    const params = {
      PrimarySiteId: siteId,
      ServingDT: date,
      IsSupper: isSupper,
    };

    const reportResponse = await MPWWebApi.postGenericInsertUpdate(
      `DailyDeposit/GetBankDepositReport`,
      params
    );

    const report = reportResponse.data.Data;

    dispatch(
      success(types.DAILY_DEPOSIT_GET_REPORT, report ? report : 'No Data')
    );
  } catch (ex) {
    const defaultError = {
      Title: TitleEnum.Error,
      Message: 'Error retrieving report.',
    };

    dispatch(
      handleErrorException(ex, types.DAILY_DEPOSIT_FETCH_ERROR, defaultError)
    );
  } finally {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, false));
  }
};

export const writeCloseDay = values => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, true));

    await MPWWebApi.postGenericInsertUpdate('DailyDeposit/WriteSave', values);

    await MPWWebApi.postGenericInsertUpdate(
      `DailyDeposit/WriteCloseDay`,
      values
    );

    dispatch(setCloseSuccessful(true));
    dispatch(
      getDailyDepositData(
        values[DailyDepositFieldEnum.Site],
        values[DailyDepositFieldEnum.Date],
        values[DailyDepositFieldEnum.IsSupper]
      )
    );
  } catch (ex) {
    const defaultError = {
      Title: TitleEnum.Error,
      Message: 'Unable to close day.',
    };

    dispatch(
      handleErrorException(ex, types.DAILY_DEPOSIT_FETCH_ERROR, defaultError)
    );
  } finally {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, false));
  }
};

export const writeSave = values => async (dispatch, getState, MPWWebApi) => {
  try {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, true));

    const response = await MPWWebApi.postGenericInsertUpdate(
      'DailyDeposit/WriteSave',
      values
    );

    const updatedDailyDeposit = response.data.Data;
    dispatch(success(types.DAILY_DEPOSIT_SAVE_SUCCESS, updatedDailyDeposit));
    // initialize with created daily deposit id
    dispatch(
      initialize(DAILY_DEPOSIT_FORM_NAME, {
        ...values,
        [DailyDepositFieldEnum.DailyDepositId]:
          updatedDailyDeposit[DailyDepositFieldEnum.DailyDepositId],
      })
    );
    dispatch(setSaveSuccessful(true));
    dispatch(
      getDailyDepositData(
        values[DailyDepositFieldEnum.Site],
        values[DailyDepositFieldEnum.Date],
        values[DailyDepositFieldEnum.IsSupper]
      )
    );
  } catch (ex) {
    const defaultError = {
      Title: TitleEnum.Error,
      Message: 'Unable to save day.',
    };
    dispatch(
      handleErrorException(ex, types.DAILY_DEPOSIT_FETCH_ERROR, defaultError)
    );
  } finally {
    dispatch(loading(types.DAILY_DEPOSIT_FETCHING, false));
  }
};

export const clearReport = () => ({
  type: types.DAILY_DEPOSIT_GET_REPORT,
  payload: null,
});

export const clearError = () => ({
  type: types.DAILY_DEPOSIT_FETCH_ERROR,
  payload: null,
});

export const clearWarnings = () => ({
  type: types.DAILY_DEPOSIT_GET_WARNINGS,
  payload: { warnings: null, CEPErrors: null },
});



// WEBPACK FOOTER //
// ./src/pages/dailydeposit/state/dailydeposit/actions.js